
/*!
 * Product Variant Additional Info Enhancer
 *
 * Purpose:
 *   - Fetches Shopify product JSON per handle to derive helper text for each
 *     variant option (e.g., save % badges or single-pack copy).
 *   - Injects that helper text under each radio-pill label so shoppers can
 *     compare options quickly; changes stay idempotent for rerenders.
 *
 * Key behaviors:
 *   - Caches product payloads and the derived helper-text map per handle to
 *     avoid redundant requests across re-initializations.
 *   - Exposes window.ProductVariantAdditionalInfo.init(root?) for manual
 *     re-runs after partial DOM updates (sections, dynamic swaps, etc.).
 *   - Auto-initializes on page load, Shopify section reloads, and variant
 *     changes while guarding against duplicate DOM nodes.
 */

(() => {
    'use strict';

    const FIELDSET_SELECTOR = 'variant-selects fieldset.product-form__input--pill';
    const COPY_SINGLE_PACK = '30 days/pack';

    // simple caches (per product handle)
    const productCache = Object.create(null); // handle -> product json
    const variantSublineCache = Object.create(null); // handle -> { "1 Pack": "...", ... }

    /* ---------------- utils ---------------- */
    const getProductHandleFromRoot = (root) => {
        // Prefer handle from a nearby product-info[data-url]
        const infoEl =
            (root && (root.querySelector?.('product-info') || root.closest?.('product-info'))) ||
            document.querySelector('product-info');

        const dataUrl = infoEl?.dataset?.url || infoEl?.getAttribute?.('data-url') || '';
        if (dataUrl) {
            const m = dataUrl.match(/\/products\/([^/?#]+)/);
            if (m) return decodeURIComponent(m[1]);
        }

        // Fallback to current URL if we're actually on a product page
        const m2 = location.pathname.match(/\/products\/([^/?#]+)/);
        return m2 ? decodeURIComponent(m2[1]) : null;
    };

    const getQtyFromTitle = (t = '') => {
        const m = String(t).match(/\d+/);
        return m ? parseInt(m[0], 10) : NaN;
    };

    const calcSavePercent  = (single, multi, qty) => {
        if (!single || !qty) return 0;
        const pct = 1 - (multi / (single * qty));
        return Math.max(0, Math.floor(pct * 100));
    };

    const fetchProduct = async (handle) => {
        if (!handle) return null;
        if (productCache[handle]) return productCache[handle];
        const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
        if (!res.ok) return null;
        const json = await res.json();
        productCache[handle] = json;
        return json;
    };

    const buildVariantSublineMap = (product) => {
        const byTitle = Object.create(null);
        product.variants.forEach(v => { byTitle[(v.title || '').trim()] = v; });

        const single =
            byTitle['1 Pack'] ||
            product.variants.find(v => /(^|\s)1\s*pack/i.test(v.title || ''));

        const map = Object.create(null);
        product.variants.forEach(v => {
            const title = (v.title || '').trim();
            const qty = getQtyFromTitle(title);
            if (qty === 1) {
                map[title] = COPY_SINGLE_PACK;
            } else if (single) {
                const pct = calcSavePercent (single.price, v.price, qty);
                map[title] = pct ? `save ${pct}%` : '';
            } else {
                map[title] = '';
            }
        });
        return map;
    };

    /* --------------- DOM Manipulation ------------------- */
    const enhanceFieldset = (fieldset, sublineByTitle) => {
        // Clean before re-append (idempotent)
        fieldset.querySelectorAll('.variant-subline').forEach(n => n.remove());
        fieldset.removeAttribute('data-subline-applied');

        const radios = fieldset.querySelectorAll('input[type="radio"]');
        for (const input of radios) {
            let label = input.nextElementSibling;
            if (!label || label.tagName !== 'LABEL') {
                label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
                if (!label) continue;
            }

            const title = (input.value || '').trim();
            const text = sublineByTitle[title] ?? '';

            const small = document.createElement('small');
            small.className = 'variant-subline';
            small.setAttribute('aria-hidden', 'true');
            small.textContent = text;
            label.appendChild(small);
        }

        fieldset.dataset.sublineApplied = '1';
    };

    const initIn = async (root = document) => {
        try {
            const handle = getProductHandleFromRoot(root);
            if (!handle) return;

            let map = variantSublineCache[handle];
            if (!map) {
                const product = await fetchProduct(handle);
                if (!product) return;
                map = variantSublineCache[handle] = buildVariantSublineMap(product);
            }

            root.querySelectorAll(FIELDSET_SELECTOR).forEach((fs) =>
                enhanceFieldset(fs, map)
            );

        } catch (err) {
            console.warn('[VariantAdditionalInfo] init failed:', err);
        }
    };


    // Public API (Expose to init from other scripts)
    window.ProductVariantAdditionalInfo = window.ProductVariantAdditionalInfo || {
        init: initIn
    };

    /* --------------- auto-boot ------------------ */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initIn());
    } else {
        initIn();
    }

    document.addEventListener('shopify:section:load', e => initIn(e.target));

    /* --- re-init on variant changes ---------------------------------------- */

    const reinitTimers = new WeakMap();

    const scheduleReinit = (root, delay = 400) => {
        clearTimeout(reinitTimers.get(root));
        const id = setTimeout(() => {
            initIn(root);
            reinitTimers.delete(root);
        }, delay);
        reinitTimers.set(root, id);
    };

    document.addEventListener('change', (e) => {
        const fieldset = e.target?.closest?.(FIELDSET_SELECTOR);
        if (!fieldset) return;

        const root = fieldset.closest('product-info') || document;
        scheduleReinit(root, 1500);
    });

})();
